import { IonText } from '@ionic/react';
import { cloneDeep, isNil, set } from 'lodash';
import { ReactElement } from 'react';
import { v4 as uuid } from 'uuid';
import { getLocalizedText } from '../../../../i18n/i18n.utils';
import { FlowItemBoundary } from '../component/flow-item/view/flow-item-boundary';
import {
  ApplicationDefaultParameterSetFormTags,
  ApplicationDefaultParameterSetTagOption,
  FlowHelperItem,
  FlowItem,
  FlowItemArrayType,
  FlowItemSection,
  FlowItemSectionParent,
  FlowItemSharedRef,
  FlowPlaceholder,
  FlowPlaceholderType,
  FlowSection,
  FlowSectionEnd,
  FlowSectionParent,
  FlowSelectedItemStats,
} from '../model/flow.types';

const instanceOfPlaceholder = (
  obj: FlowItemArrayType
): obj is FlowPlaceholder => {
  return 'identifier' in obj && obj.identifier === 'placeholder';
};

const instanceOfSectionEnd = (
  obj: FlowItemArrayType
): obj is FlowSectionEnd => {
  return 'identifier' in obj && obj.identifier === 'sectionEnd';
};

const instanceOfHelper = (obj: FlowItemArrayType): obj is FlowHelperItem => {
  return 'identifier' in obj;
};

const instanceOfFlowItem = (obj: FlowItemArrayType): obj is FlowItem => {
  return !instanceOfHelper(obj);
};

const instanceOfFlowItemSection = (
  item: FlowItemArrayType
): item is FlowItemSection => {
  return instanceOfFlowItem(item) && item.ref === FlowItemSharedRef.SECTION;
};

const instanceOfFlowItemSectionParent = (
  item: FlowItemArrayType
): item is FlowItemSectionParent => {
  if (!instanceOfFlowItemSection(item)) {
    return false;
  }

  return item.attributes?.isParent ?? false;
};

const instanceOfFlowSectionParent = (
  section: FlowSection
): section is FlowSectionParent => {
  if (section.section?.item === undefined || section.section?.item === null) {
    return false;
  }

  return instanceOfFlowItemSectionParent(section.section.item);
};

const createSectionsFromFlowItems = (
  items: FlowItemArrayType[]
): FlowSection[] => {
  const sections: FlowSection[] = [];
  let section: FlowSection = {
    items: [],
  };

  let sectionParent: FlowSectionParent | undefined = undefined;
  let unsavedSectionEndIndex: number | undefined = undefined;
  let pushSectionEnd = false;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (instanceOfFlowItemSection(item)) {
      section = {
        section: {
          item,
          index: i,
          parentItem: sectionParent?.section?.item,
        },
        items: [],
      };

      if (
        instanceOfFlowSectionParent(section) &&
        instanceOfFlowItemSectionParent(item)
      ) {
        if (isNil(item.attributes.endItemId)) {
          // if there's no endItemId, then we have an unsaved section and need
          // to find the end using the temp id's
          const foundIndex = items
            .slice(i)
            .findLastIndex(
              (child) =>
                instanceOfFlowItem(child) &&
                child.attributes?.parentSectionTempId ===
                  item.attributes?.tempId
            );

          if (foundIndex !== -1) {
            unsavedSectionEndIndex = i + foundIndex;
          } else {
            pushSectionEnd = true;
          }
        }
        sectionParent = section;
      }
      sections.push(section);
    } else {
      section.items.push({
        index: i,
        item,
      });

      if (i === 0) {
        sections.push(section);
      }
    }

    const isSpecifiedEnd =
      instanceOfFlowItem(item) &&
      !isNil(item.id) &&
      item.id === sectionParent?.section?.item.attributes?.endItemId;

    const isUnsavedSectionEnd = i === unsavedSectionEndIndex;

    const isParentEnd = isSpecifiedEnd || isUnsavedSectionEnd;

    if (isParentEnd || pushSectionEnd) {
      pushSectionEnd = false;
      unsavedSectionEndIndex = undefined;

      sections.push({
        items: [
          {
            index: -1,
            item: {
              identifier: 'sectionEnd',
              sectionId: sectionParent?.section?.item.id,
              id: uuid(),
            } as FlowSectionEnd,
          },
        ],
      });

      // if there are more items after the end of the section, create a new section
      if (i + 1 < items.length) {
        sectionParent = undefined;
        const nextItem = items[i + 1];
        const isNextItemSection = instanceOfFlowItemSection(nextItem);

        if (!isNextItemSection) {
          section = {
            items: [],
          };
          sections.push(section);
        }
      }
    }
  }

  return sections;
};

const incrementItemStats = (
  currentStats: FlowSelectedItemStats,
  isSelected?: boolean
): FlowSelectedItemStats => {
  return {
    count: currentStats.count + 1,
    selected: isSelected ? currentStats.selected + 1 : currentStats.selected,
  };
};

const generateFlowItemsWithIds = (items: FlowItem[]): FlowItem[] => {
  return items.map((i) => ({ ...i, id: i.id ?? uuid() }));
};

const buildFlowSelectedItems = (
  selectedIndex: string,
  previousSelectedItems: string[] = []
): string[] => {
  const isAlreadySelected =
    previousSelectedItems.findIndex((i) => i === selectedIndex) === -1
      ? false
      : true;

  let newSelectedItems: string[] = [];

  if (isAlreadySelected) {
    newSelectedItems = previousSelectedItems.filter((i) => i !== selectedIndex);
  } else {
    newSelectedItems = [...previousSelectedItems, selectedIndex];
  }

  return newSelectedItems;
};

const getFlowItemPlaceholderByType = (
  type: FlowPlaceholderType,
  ref?: React.Ref<HTMLDivElement>
): ReactElement | null => {
  if (type === FlowPlaceholderType.DROP) {
    return (
      <FlowItemBoundary
        ref={ref}
        text={getLocalizedText('common:end_of_changes')}
      />
    );
  } else if (type === FlowPlaceholderType.START) {
    return (
      <FlowItemBoundary text={getLocalizedText('common:start_of_changes')} />
    );
  } else if (type === FlowPlaceholderType.MIDDLE) {
    return (
      <div className="initial-placeholder">
        <IonText>
          {getLocalizedText('common:teach_initial_help_text')}
          <p>{getLocalizedText('common:teach_initial_help_text_extended')}</p>
        </IonText>
      </div>
    );
  } else {
    return null;
  }
};

const getAvailableApplicationParameterSetTagOptions = (
  options: ApplicationDefaultParameterSetTagOption[],
  tagValues?: ApplicationDefaultParameterSetFormTags
): ApplicationDefaultParameterSetTagOption[] => {
  if (tagValues === undefined) {
    return options;
  }

  const mergedTagValues: ApplicationDefaultParameterSetFormTags = {
    ...tagValues,
  };

  return options.filter((option) => {
    // we assume true so we can return at the first invalid condition
    let validOption = true;

    // these filters are or's, if all of a filter's criteria are met, then there's no need to proceed
    for (const filter of option?.filters ?? []) {
      let validFilter = true;

      // each tagFilter of the filter is an "and". all must be true for a filter to be considered valid
      for (const tagFilter of filter) {
        // the tagFilter is invalid if:
        //   1) the tag key is not in the form
        //   2) the value of the form tag is not the same as the filter
        if (
          !(tagFilter.tag in mergedTagValues) ||
          !tagFilter.filter.includes(mergedTagValues[tagFilter.tag])
        ) {
          validFilter = false;
          break;
        }
      }

      validOption = validFilter;

      // only break if the validFilter evaluates to true.
      // if false, we want the rest of the filters to be checked.
      if (validFilter) {
        validOption = true;
        break;
      }
    }

    return validOption;
  });
};

const getChildSectionsForParent = (
  parentId: string,
  sections: FlowSection[]
): FlowSection[] => {
  const childSections: FlowSection[] = [];

  let inSection = false;
  for (const section of sections) {
    if (section.section?.item.id === parentId) {
      inSection = true;
      continue;
    }

    if (
      section.items.length > 0 &&
      instanceOfSectionEnd(section.items[0]?.item)
    ) {
      inSection = false;
      continue;
    }

    if (inSection) {
      childSections.push(section);
    }
  }

  return childSections;
};

const convertSectionsToFlowItems = (
  sections: FlowSection[]
): FlowItemArrayType[] => {
  const items: FlowItemArrayType[] = [];

  for (const section of sections) {
    if (section.section) {
      items.push(section.section.item);
    }

    for (const item of section.items) {
      items.push(item.item);
    }
  }

  return items;
};

const prepareUnsavedNestedSectionItemsForSave = (
  items: FlowItem[]
): FlowItem[] => {
  const clonedItems = cloneDeep(items);

  let sectionItem: FlowItemSectionParent | undefined = undefined;

  return clonedItems.map((item, idx) => {
    if (item.id) {
      return item;
    }

    if (instanceOfFlowItemSectionParent(item)) {
      sectionItem = {
        ...item,
        id: uuid(),
      };
      return sectionItem;
    } else {
      item.id = uuid();

      // handle the children of the section
      if (sectionItem) {
        const itemIsChild =
          item.attributes?.parentSectionTempId ===
          sectionItem.attributes.tempId;

        const nextItemIsNotChild =
          items[idx + 1]?.attributes?.parentSectionTempId !==
          sectionItem.attributes.tempId;

        // modify the section item's endItemId
        if (itemIsChild && nextItemIsNotChild) {
          set(sectionItem, 'attributes.endItemId', item.id);
          delete sectionItem.attributes.tempId;
        }

        // remove the sectionParentTempId from the children
        delete item.attributes?.parentSectionTempId;

        // if the item isn't a section we can just delete the attributes
        if (!instanceOfFlowItemSection(item)) {
          delete item.attributes;
        }
      }

      return item;
    }
  });
};

const processItemsForSave = (items: FlowItem[]): FlowItem[] => {
  const hasUnsavedNestedSections = items
    .filter(instanceOfFlowItemSectionParent)
    .some((i) => i.id === undefined);

  if (hasUnsavedNestedSections) {
    return prepareUnsavedNestedSectionItemsForSave(items);
  }

  return generateFlowItemsWithIds(items);
};

const getParentIdToChildIds = (items: FlowItem[]): Record<string, string[]> => {
  const parentToChildrenIds: Record<string, string[]> = {};

  let parentItem: FlowItemSectionParent | undefined = undefined;
  for (const item of items) {
    if (instanceOfFlowItemSectionParent(item)) {
      parentToChildrenIds[item.id] = [];
      parentItem = item;
      continue;
    }

    if (parentItem) {
      parentToChildrenIds[parentItem.id].push(item.id ?? 'unknown');
    }

    if (item?.id && item.id === parentItem?.attributes.endItemId) {
      parentItem = undefined;
    }
  }

  return parentToChildrenIds;
};

export {
  buildFlowSelectedItems,
  convertSectionsToFlowItems,
  createSectionsFromFlowItems,
  generateFlowItemsWithIds,
  getAvailableApplicationParameterSetTagOptions,
  getFlowItemPlaceholderByType,
  getChildSectionsForParent,
  getParentIdToChildIds,
  incrementItemStats,
  instanceOfFlowItem,
  instanceOfFlowSectionParent,
  instanceOfHelper,
  instanceOfPlaceholder,
  instanceOfFlowItemSection,
  instanceOfSectionEnd,
  instanceOfFlowItemSectionParent,
  prepareUnsavedNestedSectionItemsForSave,
  processItemsForSave,
};
